import { useState } from 'react';
import { Star, ExternalLink, Trash2, Pencil, ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils.js';
import { Button } from '@/components/ui/button.js';
import { useContactStore } from '@/stores/contactStore.js';
import type { Contact } from '@/types/contact.js';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar.js';
import { EditContactDialog } from './EditContactDialog.js';

interface ContactItemProps {
    contact: Contact;
}

export function ContactItem({ contact }: ContactItemProps) {
    const toggleFavorite = useContactStore((state) => state.toggleFavorite);
    const removeContact = useContactStore((state) => state.removeContact);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [expanded, setExpanded] = useState(false);

    const displayName = contact.agentCard.name;
    const iconUrl = contact.agentCard.iconUrl;
    const initials = displayName
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);

    const handleToggleFavorite = (e: React.MouseEvent) => {
        e.stopPropagation();
        toggleFavorite(contact.id);
    };

    const handleRemove = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (confirm(`Remove ${displayName} from contacts?`)) {
            removeContact(contact.id);
        }
    };

    const handleOpenUrl = (e: React.MouseEvent) => {
        e.stopPropagation();
        window.open(contact.agentCard.url, '_blank');
    };

    const handleEdit = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowEditDialog(true);
    };

    return (
        <>
            <EditContactDialog
                contact={contact}
                open={showEditDialog}
                onOpenChange={setShowEditDialog}
            />
            <div
                className={cn(
                    'group relative flex flex-col rounded-lg transition-all border',
                    'hover:bg-muted cursor-pointer',
                    expanded ? 'bg-muted border-border' : 'border-transparent hover:border-border/50'
                )}
            >
                {/* Compact header - always visible */}
                <div
                    className="flex items-center gap-3 p-2"
                    onClick={() => setExpanded(!expanded)}
                >
                    {/* Avatar */}
                    <Avatar className="h-8 w-8 flex-shrink-0">
                        {iconUrl && <AvatarImage src={iconUrl} alt={displayName} />}
                        <AvatarFallback className="bg-primary/10 text-primary text-xs">
                            {initials}
                        </AvatarFallback>
                    </Avatar>

                    {/* Name and favorite */}
                    <div className="flex-1 min-w-0 flex items-center gap-2">
                        <h3 className="text-sm font-medium text-foreground truncate">
                            {displayName}
                        </h3>
                        {contact.isFavorite && (
                            <Star className="h-3 w-3 text-yellow-500 fill-yellow-500 flex-shrink-0" />
                        )}
                    </div>

                    {/* Expand indicator */}
                    <div className="flex-shrink-0 text-muted-foreground">
                        {expanded ? (
                            <ChevronDown className="h-4 w-4" />
                        ) : (
                            <ChevronRight className="h-4 w-4" />
                        )}
                    </div>
                </div>

                {/* Expanded content */}
                {expanded && (
                    <div className="px-2 pb-2 space-y-3 animate-fade-in overflow-hidden">
                        {/* Description */}
                        <p className="text-xs text-muted-foreground px-1 break-words">
                            {contact.agentCard.description}
                        </p>

                        {/* Skills */}
                        {contact.agentCard.skills.length > 0 && (
                            <div className="flex flex-wrap gap-1 overflow-hidden">
                                {contact.agentCard.skills.map((skill) => (
                                    <span
                                        key={skill.id}
                                        className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-primary/5 text-primary truncate max-w-full"
                                    >
                                        {skill.name}
                                    </span>
                                ))}
                            </div>
                        )}

                        {/* Actions */}
                        <div className="flex flex-wrap gap-1 pt-1">
                            <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 flex-1 min-w-[90px] text-xs"
                                onClick={handleToggleFavorite}
                            >
                                <Star
                                    className={cn(
                                        'h-3.5 w-3.5 mr-1 flex-shrink-0',
                                        contact.isFavorite
                                            ? 'text-yellow-500 fill-yellow-500'
                                            : 'text-muted-foreground'
                                    )}
                                />
                                <span className="truncate">{contact.isFavorite ? 'Unfavorite' : 'Favorite'}</span>
                            </Button>
                            <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 flex-1 min-w-[70px] text-xs"
                                onClick={handleEdit}
                            >
                                <Pencil className="h-3.5 w-3.5 mr-1 flex-shrink-0 text-muted-foreground" />
                                <span className="truncate">Edit</span>
                            </Button>
                            <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 flex-1 min-w-[70px] text-xs"
                                onClick={handleOpenUrl}
                            >
                                <ExternalLink className="h-3.5 w-3.5 mr-1 flex-shrink-0 text-muted-foreground" />
                                <span className="truncate">Open</span>
                            </Button>
                            <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 px-3 text-xs text-destructive hover:text-destructive flex-shrink-0"
                                onClick={handleRemove}
                            >
                                <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}
